'use client';

import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';
import Card from '../../../components/common/cards/Card/Card';
import { INewsItemEntry } from '../../../contentful/types/INewsItemEntry';
import { PageRoutes } from '../../../features/app/pages/domain/constants/PageRoutes';
import { getCardImageSource } from '../../../utils/getCardImageSource';
import { UnstyledUl } from '../List';
import { Css, Responsive } from '../../utils';
import { Color } from '../../../styles/Color';
import { ArrowIconLight } from '../Icon';
import Button from '../../../components/common/button/Button';
import {
	ItemSectionSubtitle,
	ItemSectionTitle,
	Section,
} from '../../../features/content-sections/ui/components/Section';
import { RouterLink } from '../../../components/common/link/RouterLink';
import { IBlogItemEntry } from '../../../contentful/types/IBlogItemEntry';
import { IContentCategoryEntry } from '../../../contentful/types/IContentCategoryEntry';

export interface ICarouselProps {
	title: string;
	subtitle?: string;
	description?: string;
	items: INewsItemEntry[] | IBlogItemEntry[];
	root?: PageRoutes;
	categories?: IContentCategoryEntry[];
}

export default function Carousel({
	title,
	subtitle,
	description,
	items,
	root,
	categories,
}: ICarouselProps) {
	const [currentIndex, setCurrentIndex] = useState(0);

	const containerRef = useRef<HTMLUListElement | null>(null);

	const handleDotClick = (index: number) => {
		setCurrentIndex(index);
		if (containerRef.current) {
			// Scroll to the clicked dot's position (i.e., the card)
			containerRef.current.scrollTo({
				left: (containerRef.current.children[index] as HTMLElement).offsetLeft,
				behavior: 'smooth',
			});
		}
	};

	const handlePrevClick = () => {
		setCurrentIndex((prevIndex) => Math.max(prevIndex - 1, 0));
	};

	const handleNextClick = () => {
		setCurrentIndex((prevIndex) => Math.min(prevIndex + 1, items.length - 1));
	};

	useEffect(() => {
		if (containerRef.current) {
			containerRef.current.scrollTo({
				left:
					(containerRef.current.children[currentIndex] as HTMLElement)
						?.offsetLeft || 0,
				behavior: 'smooth',
			});
		}
	}, [currentIndex]);

	return (
		<>
			<CarouselWrapper ref={containerRef}>
				<CarouselHeaderWrapper>
					<CarouselHeader>
						<div>
							{subtitle && (
								<StyledItemSectionSubtitle>
									{subtitle}
								</StyledItemSectionSubtitle>
							)}

							<StyledItemSectionTitle>{title}</StyledItemSectionTitle>

							{description && (
								<StyledItemSectionDescription>
									{description}
								</StyledItemSectionDescription>
							)}
							{root && (
								<ViewAllButton href={root} aria-label="View all card items">
									View all <ArrowIconLight aria-hidden="true" />
								</ViewAllButton>
							)}
						</div>
					</CarouselHeader>
				</CarouselHeaderWrapper>

				{items?.map((item: INewsItemEntry | IBlogItemEntry) => (
					<Card
						key={item.id}
						title={item.fields.title}
						body={item.fields.body}
						imageSource={getCardImageSource(item.fields.image)}
						imageAlt={
							item.fields.image?.fields.description ??
							item.fields.image?.fields.title
						}
						linkHref={`${root}/item/${item.fields.slug}`}
						publicationDate={item.fields.publicationDate}
						categories={
							item.contentType === 'news-item'
								? item.fields.categories
								: categories
						}
						isBackgroundSolid
					/>
				))}
			</CarouselWrapper>
			<NavigationSection>
				<NavigationContainer>
					<Button
						disabled={currentIndex === 0}
						aria-disabled={currentIndex === 0}
						aria-label="Previous"
						buttonType="primary"
						icon={<ArrowIconLight style={{ transform: 'rotate(180deg)' }} />}
						iconPosition="left"
						onClick={handlePrevClick}
					/>

					<DotsContainer>
						{Array.from({ length: items.length }).map((_, index) => (
							<Dot
								active={currentIndex === index}
								onClick={() => handleDotClick(index)}
							/>
						))}
					</DotsContainer>

					<Button
						disabled={currentIndex === items.length - 1}
						aria-disabled={currentIndex === items.length - 1}
						buttonType="primary"
						aria-label="Next"
						icon={<ArrowIconLight />}
						onClick={handleNextClick}
					/>
				</NavigationContainer>
			</NavigationSection>
		</>
	);
}

const CarouselWrapper = styled(UnstyledUl)`
	display: flex;
	flex-wrap: nowrap;
	overflow-x: auto;
	margin: 0;

	/* Hide the scrollbar */
	&::-webkit-scrollbar {
		display: none;
	}

	/* Hide the scrollbar (firefox) */
	scrollbar-width: none;

	li {
		flex: 0 0 auto;
		margin: ${Css.pxToRem(64)} ${Css.pxToRem(24)} ${Css.pxToRem(64)} 0;
		max-width: ${Css.pxToRem(360)};
	}

	li:nth-child(2) {
		margin-left: ${Css.pxToRem(-45)};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		background-color: ${Color.Neutral94};

		li {
			max-width: ${Css.pxToRem(250)};
			width: 100%;
			margin: ${Css.pxToRem(230)} ${Css.pxToRem(24)} ${Css.pxToRem(64)} 0;
		}

		li:nth-child(2) {
			margin-left: ${Css.pxToRem(30)};
		}
	}
`;

const CarouselHeaderWrapper = styled.div`
	background-color: ${Color.Neutral94};
	border-top-right-radius: ${Css.pxToRem(16)};
	border-bottom-right-radius: ${Css.pxToRem(16)};

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		position: absolute;
		margin: ${Css.pxToRem(24)} ${Css.pxToRem(16)} ${Css.pxToRem(16)};
	}
`;

const initialSpaceForCarouselCards = `${Css.pxToRem(900)}`;

const CarouselHeader = styled.header`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 100%;
	min-width: calc(100vw - ${initialSpaceForCarouselCards});
	height: 100%;

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		min-width: inherit;
		padding: ${Css.pxToRem(16)};
		align-items: flex-start;
		justify-content: flex-start;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
	}

	div {
		display: flex;
		flex-direction: column;
		width: ${Css.pxToRem(300)};
	}
`;

const StyledItemSectionSubtitle = styled(ItemSectionSubtitle)`
	color: ${Color.PrimaryBlue};
	letter-spacing: 1px;
`;

const StyledItemSectionTitle = styled(ItemSectionTitle)`
	font-size: ${Css.pxToRem(52)};
`;

const StyledItemSectionDescription = styled.p`
	margin-bottom: ${Css.pxToRem(16)};
`;

const ViewAllButton = styled(RouterLink)`
	text-decoration: none;
	font-weight: 700;
	color: ${Color.PrimaryBlue};
	display: flex;
	align-items: center;
	gap: 8px;
	align-self: baseline;

	svg {
		path {
			fill: currentColor;
		}
	}

	&:hover {
		color: ${Color.Neutral40};
	}
`;

const NavigationSection = styled(Section)`
	position: relative;
	margin-top: ${Css.pxToRem(-82)};

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		margin-top: ${Css.pxToRem(-75)};
	}
`;

const NavigationContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: ${Css.pxToRem(24)};
	margin-top: 10px;

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		justify-content: center;
		background: ${Color.Neutral94};
		padding: 0 ${Css.pxToRem(30)} ${Css.pxToRem(20)};
		margin: ${Css.pxToRem(15)} ${Css.pxToRem(-20)} 0px ${Css.pxToRem(-25)};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		justify-content: space-between;
	}

	button {
		padding: ${Css.pxToRem(8)};
		height: ${Css.pxToRem(40)};

		&:disabled svg path {
			fill: ${Color.Neutral70};
		}
	}
`;

const DotsContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
`;

const Dot = styled.div<{ active: boolean }>`
	width: ${({ active }) => (active ? Css.pxToRem(10) : Css.pxToRem(4))};
	height: ${({ active }) => (active ? Css.pxToRem(10) : Css.pxToRem(4))};
	border-radius: 50%;
	background-color: ${({ active }) =>
		active ? `${Color.PrimaryWhite}` : `${Color.Neutral80}`};
	border: ${({ active }) =>
		active ? `1px solid ${Color.PrimaryBlue}` : `1px solid ${Color.Neutral80}`};
	margin: 0 5px;
	cursor: pointer;

	&:hover {
		background-color: ${Color.PrimaryBlue};
	}
`;
